import { Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import {
  selectHasSelection,
  selectIsPackageDirtyFlag,
  selectIsRedoAvailable,
  selectIsRunningValidationFlag,
  selectIsUndoAvailable,
  selectPackage,
} from '../../store/package-designer.selectors';
import {
  createNoteAction,
  duplicateComponentsAction,
  exportJSON,
  openComponentsModal,
  openPackageImportModal,
  openPackageVariablesModal,
  openPackageVersionMessageModal,
  redoAction,
  removeComponents,
  savePackage,
  selectAllComponents,
  selectConnectedComponents,
  undoAction,
  unselectAllComponents,
} from '../../store/package-designer.actions';
import { openJobsModal } from '../../../jobs/store/jobs.actions';
import { openPackagesModal, openPackageVersionsModal } from '../../../packages/store/packages.actions';
import { NotifyService } from '../../../common/services/notify.service';

@Component({
  selector: 'designer-controls',
  template: `
    <fieldset [disabled]="isVersionView" *ngIf="package$ | async as packageItem">
      <div id="designer-controls" class="designer-controls">
        <div class="pull-left designer-controls-main">
          <button type="button" class="btn btn-default btn-lg margin-right" (click)="editPackage()">
            {{ packageItem.name | xpLengthCheck: 40 }} <i class="fa fa-pencil" aria-hidden="true"></i>
          </button>
          <br *ngxPermissionsOnly="'versionControl'" />
          <button
            *ngxPermissionsOnly="'versionControl'"
            type="button"
            class="btn btn-default btn-rollback btn-version-validate"
            (click)="openVersionsModal()"
            matTooltip="Rollback to previous version"
            matTooltipPosition="right"
            matTooltipClass="right"
          >
            Ver. {{ packageItem.package_version }} <i class="fa fa-undo"></i>
          </button>
        </div>
        <div class="btn-group btn-group-lg pull-right">
          <button
            *ngIf="isPackageDirty$ | async as isPackageDirty"
            type="button"
            class="btn btn-info btn-lg btn-run-job"
            (click)="runJob(isPackageDirty)"
          >
            {{ 'Save & Run job' }}
          </button>
          <button
            *ngIf="!(isPackageDirty$ | async)"
            type="button"
            class="btn btn-info btn-lg btn-run-job"
            (click)="runJob()"
          >
            {{ 'Run job' }}
          </button>
        </div>
        <div *ngxPermissionsExcept="'versionControl'" class="btn-group btn-group-lg pull-right margin-right">
          <button type="button" class="btn btn-default btn-save" (click)="save()">
            Save
          </button>
        </div>
        <div *ngxPermissionsOnly="'versionControl'" class="btn-group btn-group-lg pull-right margin-right dropdown">
          <button
            id="import-export-select"
            type="button"
            class="btn btn-default dropdown-toggle btn-save"
            [ngClass]="{ 'btn-success': isPackageDirty$ | async }"
            [matMenuTriggerFor]="saveDropdown"
          >
            Save
          </button>
          <mat-menu
            #saveDropdown="matMenu"
            class="dropdown-menu-new wide-elements"
            aria-labelledby="import-export-select"
          >
            <li mat-menu-item [ngClass]="{ disabled: !(isPackageDirty$ | async) }">
              <a class="link-save" (click)="save()"> <i class="fa fa-save"></i> Quick save </a>
            </li>
            <li mat-menu-item>
              <a (click)="createMajorVersion()"> <i class="fa fa-code-fork"></i> Create Major Version </a>
            </li>
          </mat-menu>
        </div>
        <div class="btn-group btn-group-lg pull-right margin-right" *ngIf="!(isPackageValidating$ | async)">
          <button
            type="button"
            class="btn btn-default btn-validate"
            (click)="validate()"
            matTooltip="Save and validate"
            matTooltipPosition="below"
            matTooltipClass="below"
          >
            <i class="fa fa-check-circle validation-icon"></i>
          </button>
        </div>
        <div class="btn-group btn-group-lg pull-right margin-right" *ngIf="isPackageValidating$ | async">
          <button
            type="button"
            class="btn btn-default btn-validate"
            matTooltip="Validation in progress"
            matTooltipPosition="below"
            matTooltipClass="below"
            [disabled]="true"
          >
            <i class="fa fa-refresh fa-spin fa-fw validation-icon"></i>
          </button>
        </div>
        <div class="btn-group btn-group-lg pull-right margin-right">
          <button
            type="button"
            class="btn btn-default btn-lg btn-settings"
            (click)="packageVariables()"
            matTooltip="Set variables"
            matTooltipPosition="below"
            matTooltipClass="below"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="4px" viewBox="0 0 20 4">
              <g>
                <circle style="fill: #3f3f3f;" cx="2" cy="2" r="2" />
                <circle style="fill: #3f3f3f;" cx="10" cy="2" r="2" />
                <circle style="fill: #3f3f3f;" cx="18" cy="2" r="2" />
              </g>
            </svg>
          </button>
        </div>
        <div class="btn-group btn-group-lg pull-right margin-right">
          <button
            type="button"
            class="btn btn-default btn-undo"
            (click)="undo()"
            [disabled]="!(isUndoAvailable$ | async)"
            matTooltip="Undo"
            matTooltipPosition="below"
            matTooltipClass="below"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16.9px" height="16.3px" viewBox="0 0 16.9 16.3">
              <path
                id="XMLID_1_"
                style="fill: none; stroke: #3f3f3f; stroke-miterlimit: 10;"
                d="M9.4,5.2H6.6v-4L0.7,7.1l5.9,5.8V9.2h2.8
                c3.3,0,6,2.1,6.8,5.1c0.1-0.6,0.2-1.1,0.2-1.8C16.4,8.7,13.2,5.2,9.4,5.2z"
              />
            </svg>
          </button>
          <button
            type="button"
            class="btn btn-default btn-redo"
            (click)="redo()"
            [disabled]="!(isRedoAvailable$ | async)"
            matTooltip="Redo"
            matTooltipPosition="below"
            matTooltipClass="below"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16.3px" viewBox="0 0 16 16.3">
              <path
                id="XMLID_1_"
                style="fill: none; stroke: #3f3f3f; stroke-miterlimit: 10;"
                d="M7.5,5.2h1.9v-4l5.9,5.9l-5.9,5.8V9.2H7.5
                c-3.3,0-6,2.1-6.8,5.1c-0.1-0.6-0.2-1.1-0.2-1.8C0.5,8.7,3.6,5.2,7.5,5.2z"
              />
            </svg>
          </button>
        </div>
        <div class="btn-group btn-group-lg pull-right margin-right has-selection" *ngIf="hasSelection$ | async">
          <button
            type="button"
            class="btn btn-default"
            (click)="duplicateSelectedComponents()"
            matTooltip="Copy"
            matTooltipPosition="below"
            matTooltipClass="below"
          >
            <i class="fa fa-copy"></i>
          </button>
          <button
            type="button"
            class="btn btn-default"
            (click)="deleteSelectedComponents()"
            matTooltip="Remove"
            matTooltipPosition="below"
            matTooltipClass="below"
          >
            <i class="fa fa-trash text-danger"></i>
          </button>
        </div>
        <div class="btn-group btn-group-lg pull-right margin-right dropdown">
          <button
            id="components-select"
            type="button"
            class="btn btn-default dropdown-toggle"
            [matMenuTriggerFor]="selectDropdown"
          >
            Select <span class="badge components-select-badge"></span>
          </button>
          <mat-menu #selectDropdown="matMenu" class="dropdown-menu-new" aria-labelledby="components-select">
            <li mat-menu-item><a (click)="selectAllComponents()">All</a></li>
            <li mat-menu-item><a (click)="selectConnectedComponents()">Connected</a></li>
            <li mat-menu-item><a (click)="unselectAllComponents()">None</a></li>
          </mat-menu>
        </div>
        <div *ngxPermissionsExcept="'sourceControl'" class="btn-group btn-group-lg pull-right margin-right dropdown">
          <button
            id="import-export-select"
            type="button"
            class="btn btn-default dropdown-toggle"
            [matMenuTriggerFor]="importDropdown"
          >
            <i class="fa fa-code"></i> Import/Export
          </button>
          <mat-menu #importDropdown="matMenu" class="dropdown-menu-new" aria-labelledby="import-export-select">
            <li mat-menu-item>
              <a (click)="promoteSourceControlFeature()"><i class="fa fa-file-import"></i> Import JSON</a>
            </li>
            <li mat-menu-item>
              <a (click)="promoteSourceControlFeature()"><i class="fa fa-file-export"></i> Export JSON</a>
            </li>
          </mat-menu>
        </div>
        <div *ngxPermissionsOnly="'sourceControl'" class="btn-group btn-group-lg pull-right margin-right dropdown">
          <button
            id="import-export-select"
            type="button"
            class="btn btn-default dropdown-toggle"
            [matMenuTriggerFor]="importSourceControlDropdown"
          >
            <i class="fa fa-code"></i> Import/Export
          </button>
          <mat-menu
            #importSourceControlDropdown="matMenu"
            class="dropdown-menu-new"
            aria-labelledby="import-export-select"
          >
            <li mat-menu-item>
              <a (click)="importJSON()"><i class="fa fa-file-import"></i> Import JSON</a>
            </li>
            <li mat-menu-item>
              <a class="link-version-validate" (click)="exportJSON()"><i class="fa fa-file-export"></i> Export JSON</a>
            </li>
          </mat-menu>
        </div>
        <div class="btn-group btn-group-lg pull-right margin-right">
          <button
            type="button"
            class="btn btn-default btn-comment"
            (click)="addNote()"
            matTooltip="Add a Note"
            matTooltipPosition="below"
            matTooltipClass="below"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="23px" height="22px" viewBox="0 0 23 22">
              <g id="icon-package-comment">
                <polygon
                  style="fill: none; stroke: #3f3f3f; stroke-miterlimit: 10;"
                  points="0.5,0.5 0.5,16.5 3.5,16.5 3.5,21 10,16.5 22.5,16.5
                        22.5,0.5    "
                />
                <polygon
                  style="fill: #3f3f3f;"
                  points="15,8 12,8 12,5 11,5 11,8 8,8 8,9 11,9 11,12 12,12 12,9 15,9  "
                />
              </g>
            </svg>
          </button>
        </div>
        <div class="btn-group btn-group-lg pull-right margin-right">
          <button
            *ngIf="packageItem.flow_type === 'dataflow'"
            type="button"
            class="btn btn-default btn-add-component"
            (click)="addComponent()"
            id="add-component"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="13px" height="13px" viewBox="0 0 13 13">
              <polygon style="fill: #3f3f3f;" points="10,4 6,4 6,0 4,0 4,4 0,4 0,6 4,6 4,10 6,10 6,6 10,6 " />
            </svg>
            Add component
          </button>
          <button
            *ngIf="packageItem.flow_type === 'workflow'"
            type="button"
            class="btn btn-default btn-add-component"
            (click)="addComponent(true)"
            id="add-component"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="13px" height="13px" viewBox="0 0 13 13">
              <polygon style="fill: #3f3f3f;" points="10,4 6,4 6,0 4,0 4,4 0,4 0,6 4,6 4,10 6,10 6,6 10,6 " />
            </svg>
            Add task
          </button>
        </div>
      </div>
    </fieldset>
  `,
})
export class DesignerControlsComponent {
  package$ = this.store.select(selectPackage);
  hasSelection$ = this.store.select(selectHasSelection);
  isUndoAvailable$ = this.store.select(selectIsUndoAvailable);
  isRedoAvailable$ = this.store.select(selectIsRedoAvailable);
  isPackageDirty$ = this.store.select(selectIsPackageDirtyFlag);
  isPackageValidating$ = this.store.select(selectIsRunningValidationFlag);
  isVersionView = false;

  constructor(private store: Store<AppState>, private notifyService: NotifyService) {}

  addComponent(isWorkflow = false) {
    this.store.dispatch(
      openComponentsModal({
        flags: {
          hasSources: !isWorkflow,
          hasTransformations: !isWorkflow,
          hasDestinations: !isWorkflow,
          hasWorkflow: isWorkflow,
        },
      }),
    );
  }

  duplicateSelectedComponents() {
    this.store.dispatch(duplicateComponentsAction({}));
  }

  deleteSelectedComponents() {
    this.store.dispatch(removeComponents({}));
  }

  undo() {
    this.store.dispatch(undoAction());
  }

  redo() {
    this.store.dispatch(redoAction());
  }

  selectAllComponents() {
    this.store.dispatch(selectAllComponents());
  }

  selectConnectedComponents() {
    this.store.dispatch(selectConnectedComponents());
  }

  unselectAllComponents() {
    this.store.dispatch(unselectAllComponents());
  }

  packageVariables() {
    this.store.dispatch(openPackageVariablesModal());
  }

  runJob(isPackageDirty?: boolean) {
    if (isPackageDirty) {
      this.store.dispatch(savePackage({ openJobModal: true }));
    } else {
      this.store.dispatch(openJobsModal());
    }
  }

  editPackage() {
    this.store.dispatch(openPackagesModal());
  }

  openVersionsModal() {
    this.store.dispatch(openPackageVersionsModal());
  }

  addNote() {
    this.store.dispatch(createNoteAction());
  }

  promoteSourceControlFeature() {
    this.notifyService.info(
      `Please contact <a class="support-link">Support</a> or your Account Manager to activate Source Control feature.`,
      'Source Control is disabled',
    );

    setTimeout(() => {
      document.querySelector('.support-link').addEventListener('click', function () {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (window['Intercom']) {
          // eslint-disable-next-line @typescript-eslint/dot-notation
          window['Intercom']('show');
        }
      });
    }, 100);
  }

  exportJSON() {
    this.store.dispatch(exportJSON());
  }

  importJSON() {
    this.store.dispatch(openPackageImportModal());
  }

  createMajorVersion() {
    this.store.dispatch(openPackageVersionMessageModal());
  }

  save() {
    this.store.dispatch(savePackage({}));
  }

  validate() {
    this.store.dispatch(savePackage({ runValidation: true }));
  }
}
